import React, {useEffect, useState} from 'react';
import {FiCheck, FiDownload, FiEdit, FiSend, FiX} from 'react-icons/fi';
import './ContractModal.css';
import SignatureCanvas from './SignatureCanvas';
import {BASE_URL} from "../../Constants";

function ContractModal({isOpen, onClose, clientData}) {
    const [contractData, setContractData] = useState({
        contractDate: new Date().toISOString().split('T')[0],
        deliveryDate: new Date().toISOString().split('T')[0],
        furnitureDescription: clientData?.furnitureType || 'Mobilier personalizat',
        totalAmount: clientData?.total || '0',
        advancePayment: clientData?.advance || '0',
        preInstallationPayment: '0',
        executionDays: '',
        installationAddress: clientData?.address || '',
        specifications: 'Specificații conform discuțiilor cu clientul',
        workDescription: 'Masurare, proiectare, fabricatie, transport si montaj mobilier',
        materialDescription: 'Materialele sunt furnizate de Beneficiar de la furnizorii agreati',
        deliveryTerms: 'Mobilierul va fi livrat si montat la adresa specificata in contract.',
        paymentTerms: 'Plata se face in etape: avans la semnarea contractului, plata pre-montaj cu 1 zi inainte, rest la finalizare.',
        warrantyTerms: 'Garantie pentru calitatea executiei si montajului. Nu acopera materialele (garantate de furnizori).',
        additionalTerms: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [remainingAmount, setRemainingAmount] = useState(0);
    const [showSignatureCanvas, setShowSignatureCanvas] = useState(false);
    const [createdContract, setCreatedContract] = useState(null);
    const [contractSigned, setContractSigned] = useState(false);
    const [signatureData, setSignatureData] = useState(null);
    const [isCreatingContract, setIsCreatingContract] = useState(false);
    const [dataInitialized, setDataInitialized] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);


    // Calculate remaining amount when amounts change
    useEffect(() => {
        const total = parseFloat(contractData.totalAmount) || 0;
        const advance = parseFloat(contractData.advancePayment) || 0;
        setRemainingAmount(total - advance);
    }, [contractData.totalAmount, contractData.advancePayment]);

    useEffect(() => {
        if (clientData && isOpen) {
            setContractData(prev => ({
                ...prev,
                furnitureDescription: clientData.furnitureType || prev.furnitureDescription,
                totalAmount: clientData.total || prev.totalAmount,
                advancePayment: clientData.advance || prev.advancePayment
            }));

            // Marchează că datele au fost inițializate
            setDataInitialized(true);
        }
    }, [clientData, isOpen]);

    // Generate contract DOAR după ce datele au fost inițializate
    useEffect(() => {
        if (isOpen && clientData?.id && !createdContract && dataInitialized) {
            generateContract();
        }
    }, [isOpen, clientData?.id, dataInitialized]);

    // Reset states when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setError('');
            setSuccess('');
            setContractSigned(false);
            setSignatureData(null);
            setCreatedContract(null);
            setShowSignatureCanvas(false);
            setIsCreatingContract(false);
            setDataInitialized(false); // Reset și flag-ul de inițializare
        }
    }, [isOpen]);

    const isMobileDevice = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            (window.innerWidth <= 768);
    };

    const handleViewContract = async () => {
        if (!createdContract) {
            setError('Contractul nu a fost generat încă.');
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${BASE_URL}/contract/${createdContract.id}/pdf`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            // On mobile devices, open PDF in new tab instead of iframe modal
            if (isMobileDevice()) {
                // Create a temporary link to open PDF in new tab
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                setSuccess('Contractul s-a deschis într-un tab nou. Pe dispozitivele mobile, PDF-ul se deschide în aplicația nativă pentru o experiență mai bună.');

                // Clean up blob URL after a delay
                setTimeout(() => {
                    URL.revokeObjectURL(url);
                }, 2000);
            } else {
                // On desktop, use iframe modal
                setPdfUrl(url);
                setShowViewModal(true);
            }
        } catch (err) {
            console.error('Error viewing contract:', err);
            setError('Eroare la vizualizarea contractului: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };


    const validateForm = () => {
        if (!contractData.contractDate) {
            setError('Data contractului este obligatorie');
            return false;
        }
        if (!contractData.deliveryDate) {
            setError('Data livrării este obligatorie');
            return false;
        }
        if (!contractData.furnitureDescription.trim()) {
            setError('Descrierea mobilierului este obligatorie');
            return false;
        }
        const totalAmount = parseFloat(contractData.totalAmount);
        if (isNaN(totalAmount) || totalAmount <= 0) {
            setError('Suma totală trebuie să fie mai mare decât 0');
            return false;
        }
        const advancePayment = parseFloat(contractData.advancePayment);
        if (isNaN(advancePayment) || advancePayment <= 0) {
            setError('Avansul trebuie să fie mai mare decât 0');
            return false;
        }
        if (parseFloat(contractData.advancePayment) > parseFloat(contractData.totalAmount)) {
            setError('Avansul nu poate fi mai mare decât suma totală');
            return false;
        }
        if (!clientData?.id) {
            setError('Clientul trebuie să fie salvat înainte de generarea contractului');
            return false;
        }
        return true;
    };

    const generateContract = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setIsCreatingContract(true);
            setError('');
            setSuccess('');

            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Token de autentificare lipsește');
            }

            // Prepare contract request
            const contractRequest = {
                customerId: clientData.id,
                totalAmount: parseFloat(contractData.totalAmount) || 0,
                advancePayment: parseFloat(contractData.advancePayment) || 0,
                specifications: contractData.specifications,
                workDescription: contractData.workDescription,
                materialDescription: contractData.materialDescription,
                deliveryTerms: contractData.deliveryTerms,
                paymentTerms: contractData.paymentTerms,
                warrantyTerms: contractData.warrantyTerms,
                additionalTerms: contractData.additionalTerms,
                preInstallationPayment: contractData.preInstallationPayment ? parseFloat(contractData.preInstallationPayment) : null,
                executionDays: contractData.executionDays ? parseInt(contractData.executionDays) : null,
                installationAddress: contractData.installationAddress || null
            };

            console.log('Creating contract with data:', contractRequest);

            // Create contract
            const createResponse = await fetch(`${BASE_URL}/contract`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(contractRequest)
            });

            if (!createResponse.ok) {
                const errorData = await createResponse.text();
                throw new Error(`HTTP error! status: ${createResponse.status}, message: ${errorData}`);
            }

            const contract = await createResponse.json();
            console.log('Contract created:', contract);

            setCreatedContract(contract);
            setSuccess(`Contract generat cu succes! Numărul contractului: ${contract.contractNumber}`);

            // Check if contract is already signed
            if (contract.status && contract.status !== 'GENERATED') {
                setContractSigned(true);
            }

        } catch (error) {
            console.error('Error creating contract:', error);
            let errorMessage = 'A apărut o eroare la crearea contractului.';

            if (error.message.includes('401')) {
                errorMessage = 'Sesiunea a expirat. Te rugăm să te autentifici din nou.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
        } finally {
            setIsCreatingContract(false);
        }
    };

    const handleDownloadContract = async () => {
        if (!createdContract) {
            setError('Contractul nu a fost generat încă.');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const pdfResponse = await fetch(`${BASE_URL}/contract/${createdContract.id}/pdf`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!pdfResponse.ok) {
                throw new Error(`HTTP error! status: ${pdfResponse.status}`);
            }

            const pdfBlob = await pdfResponse.blob();
            const pdfUrl = URL.createObjectURL(pdfBlob);

            // Download the PDF
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `Contract_${createdContract.contractNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up
            setTimeout(() => {
                URL.revokeObjectURL(pdfUrl);
            }, 1000);

            setSuccess('Contractul a fost descărcat cu succes!');

        } catch (err) {
            console.error('Error downloading contract:', err);
            setError('A apărut o eroare la descărcarea contractului: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignContract = async () => {
        if (contractSigned) {
            setError('Contractul a fost deja semnat!');
            return;
        }

        if (!createdContract) {
            setError('Contractul nu a fost generat încă.');
            return;
        }

        // Open signature canvas
        setShowSignatureCanvas(true);
    };

    const handleSendToClient = async () => {
        if (!createdContract) {
            setError('Contractul nu a fost generat încă.');
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            // Get contract PDF
            const token = localStorage.getItem('token');
            const pdfResponse = await fetch(`${BASE_URL}/contract/${createdContract.id}/pdf`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!pdfResponse.ok) {
                throw new Error(`HTTP error! status: ${pdfResponse.status}`);
            }

            const pdfBlob = await pdfResponse.blob();

            console.log('PDF retrieved, preparing WhatsApp message...');

            // Create blob URL for the PDF
            const pdfUrl = URL.createObjectURL(pdfBlob);

            // Prepare WhatsApp message
            const clientName = `${clientData.firstName} ${clientData.lastName}`.trim();
            const phoneNumber = clientData.phone1?.replace(/\D/g, '') || clientData.phone2?.replace(/\D/g, '');

            if (!phoneNumber) {
                throw new Error('Numărul de telefon al clientului nu este disponibil');
            }

            // IMPROVED: Format phone number correctly for WhatsApp
            let formattedPhone = phoneNumber;

            // Remove any leading zeros and spaces
            formattedPhone = formattedPhone.replace(/^0+/, '');

            // For Romanian numbers, add country code if not present
            if (!formattedPhone.startsWith('40')) {
                // Romanian mobile numbers should start with 7 after removing the leading 0
                if (formattedPhone.startsWith('7')) {
                    formattedPhone = '40' + formattedPhone;
                } else {
                    // If it doesn't start with 7, it might be a landline or invalid
                    // Try to format it anyway
                    formattedPhone = '40' + formattedPhone;
                }
            }

            console.log('Original phone:', phoneNumber);
            console.log('Formatted phone for WhatsApp:', formattedPhone);

            // Create signing link for client
            const signingLink = `${BASE_URL}/contract-signing.html?contractId=${createdContract.id}`;

            const message = `Salut ${clientName}! 

Îți trimitem contractul pentru mobilierul comandat:
 - Contract Nr: ${createdContract.contractNumber}
 - Mobilier: ${contractData.furnitureDescription}
 - Valoare totală: ${contractData.totalAmount} lei
 - Avans: ${contractData.advancePayment} lei
 - Data livrării: ${new Date(contractData.deliveryDate).toLocaleDateString('ro-RO')}

🔗 Pentru a semna contractul online, accesează acest link:
${signingLink}

📄 Poți vizualiza și descărca contractul accesând link-ul de mai sus.

⚠️ IMPORTANT: După semnare, contractul va fi activ și nu va mai putea fi modificat.

Pentru întrebări, ne poți contacta oricând!`;

            // Open WhatsApp Web with message
            const encodedMessage = encodeURIComponent(message);

            // Try different WhatsApp URL formats for better compatibility
            const whatsappUrls = [
                `https://wa.me/${formattedPhone}?text=${encodedMessage}`, // Primary format
                `https://web.whatsapp.com/send?phone=${formattedPhone}&text=${encodedMessage}`, // Fallback
                `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encodedMessage}` // Alternative
            ];

            console.log('WhatsApp URLs generated:', whatsappUrls);

            // Try to open WhatsApp - start with the most compatible format
            const whatsappUrl = whatsappUrls[0]; // Use wa.me format first
            window.open(whatsappUrl, '_blank');

            setSuccess(`Mesajul cu link-ul de semnare a fost pregătit pentru ${clientName}. 
            Număr telefon formatat: +${formattedPhone}
            Link pentru semnare: ${signingLink}
            
            Dacă WhatsApp nu se deschide automat, poți copia manual mesajul și link-ul.`);

            // Clean up blob URL
            setTimeout(() => {
                URL.revokeObjectURL(pdfUrl);
            }, 1000);

        } catch (err) {
            console.error('Error sending contract to client:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignatureSave = async (signatureDataUrl) => {
        if (!createdContract) {
            setError('Nu a fost găsit contractul pentru semnare.');
            return;
        }

        if (contractSigned) {
            setError('Contractul a fost deja semnat!');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Token de autentificare lipsește');
            }

            // Convert data URL to blob
            const response = await fetch(signatureDataUrl);
            const blob = await response.blob();

            // Create form data
            const formData = new FormData();
            formData.append('signature', blob, 'signature.png');

            // Send signature to backend
            const signResponse = await fetch(
                `${BASE_URL}/contract/${createdContract.id}/sign`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                }
            );

            if (!signResponse.ok) {
                const errorData = await signResponse.text();
                throw new Error(`HTTP error! status: ${signResponse.status}, message: ${errorData}`);
            }

            const signedContract = await signResponse.json();
            console.log('Contract signed successfully:', signedContract);

            // Mark contract as signed and store signature data
            setContractSigned(true);
            setSignatureData(signatureDataUrl);
            setSuccess(`Contract semnat cu succes! Numărul contractului: ${createdContract.contractNumber}`);
            setShowSignatureCanvas(false);

        } catch (err) {
            console.error('Error signing contract:', err);
            setError('A apărut o eroare la semnarea contractului: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setError('');
        setSuccess('');
        setShowSignatureCanvas(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="contract-modal-overlay">
            <div className="contract-modal-content">
                <div className="contract-modal-header">
                    <h2>Contract Mobilier</h2>
                    <button className="contract-close-button" onClick={handleClose}>
                        <FiX/>
                    </button>
                </div>

                <div className="contract-modal-body">
                    {error && <div className="contract-error">{error}</div>}
                    {success && <div className="contract-success">{success}</div>}

                    {isCreatingContract && (
                        <div className="contract-loading">
                            <div className="contract-spinner"></div>
                            Se generează contractul...
                        </div>
                    )}

                    {createdContract && (
                        <div className="contract-details-section">
                            <h3>Detalii Contract</h3>
                            <p><strong>Numărul contractului:</strong> {createdContract.contractNumber}</p>
                            <p><strong>Client:</strong> {clientData.firstName} {clientData.lastName}</p>
                            <p><strong>Mobilier:</strong> {contractData.furnitureDescription}</p>
                            <p><strong>Valoare totală:</strong> {contractData.totalAmount} lei</p>
                            <p><strong>Avans:</strong> {contractData.advancePayment} lei</p>
                            {parseFloat(contractData.preInstallationPayment) > 0 && (
                                <p><strong>Plată pre-montaj:</strong> {contractData.preInstallationPayment} lei</p>
                            )}
                            <p><strong>Rest de plată:</strong> {remainingAmount} lei</p>
                            {contractData.executionDays && (
                                <p><strong>Zile execuție:</strong> {contractData.executionDays} zile lucrătoare</p>
                            )}
                            <p><strong>Data
                                livrării:</strong> {new Date(contractData.deliveryDate).toLocaleDateString('ro-RO')}</p>
                        </div>
                    )}

                    {/* Contract status */}
                    {contractSigned && (
                        <div className="contract-signature-info signed">
                            <h3>✓ Contract Semnat</h3>
                            <p>
                                Contractul a fost semnat cu succes și poate fi descărcat sau trimis clientului.
                            </p>
                        </div>
                    )}

                    {!contractSigned && createdContract && (
                        <div className="contract-signature-info">
                            <h3>Contract Generat</h3>
                            <p>Contractul a fost generat cu succes. Poți să îl semnezi acum sau să îl trimiți clientului
                                pentru semnătură.</p>
                        </div>
                    )}
                </div>

                <div className="contract-modal-footer">

                    <button
                        className="contract-view-button"
                        onClick={handleViewContract}
                        disabled={isLoading || isCreatingContract || !createdContract}
                        style={{
                            backgroundColor: '#9b59b6', // mov
                            color: 'white'
                        }}
                    >
                        {isLoading ? (
                            <>
                                <div className="contract-spinner"></div>
                                Se încarcă...
                            </>
                        ) : (
                            <>
                                <FiDownload/> Vizualizare
                            </>
                        )}
                    </button>

                    <button
                        className={`contract-sign-button ${contractSigned ? 'signed' : ''}`}
                        onClick={handleSignContract}
                        disabled={isLoading || isCreatingContract || contractSigned || !createdContract}
                        style={{
                            backgroundColor: contractSigned ? '#4caf50' : '#2196f3',
                            color: 'white',
                            cursor: contractSigned ? 'not-allowed' : 'pointer',
                            opacity: contractSigned || !createdContract ? 0.7 : (isLoading ? 0.6 : 1)
                        }}
                    >
                        {contractSigned ? (
                            <>
                                <FiCheck/>
                                Deja semnat
                            </>
                        ) : (
                            <>
                                <FiEdit/>
                                Semnează acum
                            </>
                        )}
                    </button>


                    <button
                        className="contract-sign-button"
                        onClick={handleDownloadContract}
                        disabled={isLoading || isCreatingContract || !createdContract}
                        style={{
                            backgroundColor: '#26c9bc',
                            color: 'white'
                        }}
                    >
                        {isLoading ? (
                            <>
                                <div className="contract-spinner"></div>
                                Se descarcă...
                            </>
                        ) : (
                            <>
                                <FiDownload/>
                                Descarcă contract
                            </>
                        )}
                    </button>

                    <button
                        className="contract-send-button"
                        onClick={handleSendToClient}
                        disabled={isLoading || isCreatingContract || !createdContract}
                    >
                        {isLoading ? (
                            <>
                                <div className="contract-spinner"></div>
                                Se trimite...
                            </>
                        ) : (
                            <>
                                <FiSend/>
                                Trimite clientului
                            </>
                        )}
                    </button>

                    <button
                        className="contract-cancel-button"
                        onClick={handleClose}
                        disabled={isLoading || isCreatingContract}
                    >
                        Închide
                    </button>
                </div>
            </div>

            {showViewModal && pdfUrl && (
                <div className="pdf-view-modal-overlay">
                    <div className="pdf-view-modal">
                        <div className="pdf-view-header">
                            <h3>Contract Nr. {createdContract.contractNumber}</h3>
                            <button onClick={() => {
                                setShowViewModal(false);
                                URL.revokeObjectURL(pdfUrl); // cleanup
                                setPdfUrl(null);
                            }} className="close-pdf-btn">✕
                            </button>
                        </div>
                        <iframe
                            src={pdfUrl}
                            title="Contract PDF"
                            style={{width: '100%', height: '90vh', border: 'none'}}
                        ></iframe>
                    </div>
                </div>
            )}

            {/* Signature Canvas Modal */}
            {showSignatureCanvas && !contractSigned && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1300,
                    padding: '20px'
                }}>
                    <SignatureCanvas
                        isOpen={true}
                        onClose={() => setShowSignatureCanvas(false)}
                        onSave={handleSignatureSave}
                        title="Semnează Contractul"
                    />
                </div>
            )}
        </div>
    );
}

export default ContractModal;